/**
 * Closing an account.
 *
 * The one action on this site that cannot be undone, so it is presented as
 * such: kept out of the way of everything else, stating plainly what goes and
 * what does not, and asking for the company's own name to be typed out rather
 * than for a button to be clicked twice. Typing the name is not a hurdle for
 * its own sake — it is the step that makes it impossible to do this by
 * accident, or to a company you did not mean.
 */

import { useState, type ReactNode } from 'react';

import { TextField } from './Fields';
import { ErrorNotice } from './Status';
import { CloseIcon } from './ui';

function Consequences({ jobCount }: { readonly jobCount: number }): ReactNode {
  return (
    <>
      <ul className="plainlist">
        <li>
          {jobCount === 0
            ? 'Your company profile is deleted.'
            : `Your company profile and ${jobCount === 1 ? 'your listing' : `all ${jobCount} of your listings`} are deleted, and removed from every leaderboard immediately.`}
        </li>
        <li>Your public company page stops working. Its address becomes available again.</li>
        <li>Your sign-in is deleted, so this email address can be registered again.</li>
      </ul>
      <p className="small muted">
        Bids already placed are not refunded. Your payment records are retained. A closed account
        and the listings it held cannot be restored.
      </p>
    </>
  );
}

export function CloseAccountDialog({
  companyName,
  jobCount,
  busy,
  error,
  signInStranded = false,
  onConfirm,
  onCancel,
}: {
  readonly companyName: string;
  readonly jobCount: number;
  readonly busy: boolean;
  readonly error: unknown;
  /** The data went, the sign-in did not. The address is not free to reuse. */
  readonly signInStranded?: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}): ReactNode {
  const [typed, setTyped] = useState('');
  const matches = typed.trim().toLowerCase() === companyName.trim().toLowerCase();

  return (
    <div
      className="modal-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Close your account"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <div className="modal is-narrow">
        <div className="modal-body">
          <div className="row" style={{ alignItems: 'flex-start', flexWrap: 'nowrap', gap: 16 }}>
            <div style={{ flexGrow: 1, minWidth: 0 }}>
              <h2>Close your account</h2>
              <p className="small muted" style={{ marginTop: 6 }}>
                This is permanent and takes effect immediately.
              </p>
            </div>
            <button
              type="button"
              className="modal-close"
              aria-label="Cancel"
              disabled={busy}
              onClick={onCancel}
            >
              <CloseIcon />
            </button>
          </div>

          <div style={{ marginTop: 20 }}>
            <Consequences jobCount={jobCount} />
          </div>

          <form
            style={{ marginTop: 22 }}
            onSubmit={(event) => {
              event.preventDefault();
              if (matches && !busy) onConfirm();
            }}
          >
            <TextField
              label="Type your company name to confirm"
              value={typed}
              onChange={setTyped}
              placeholder={companyName}
              autoComplete="off"
              disabled={busy}
              hint={`Type “${companyName}” as it appears on your profile.`}
              required
            />

            {error === undefined || error === null ? null : (
              <div style={{ marginTop: 14 }}>
                <ErrorNotice error={error} />
              </div>
            )}

            {signInStranded ? (
              <div className="notice is-caution" role="alert" style={{ marginTop: 14 }}>
                Your listings and company profile are deleted. Your sign-in could not be removed,
                so this email address is not free to register again yet — close the account again
                to clear it, or contact us before signing up with this address.
              </div>
            ) : null}

            <div className="row" style={{ gap: 10, marginTop: 20 }}>
              <button
                type="submit"
                className="pill pill-danger"
                disabled={!matches || busy}
              >
                {busy ? 'Closing your account…' : 'Close my account permanently'}
              </button>
              <button type="button" className="pill pill-ghost" disabled={busy} onClick={onCancel}>
                Keep my account
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

/** The way in: last on the dashboard, where nothing else is going on. */
export function CloseAccountSection({ onOpen }: { readonly onOpen: () => void }): ReactNode {
  return (
    <section className="card is-padded closeaccount">
      <h2>Close your account</h2>
      <p className="small muted">
        This deletes your company profile, every listing you have posted, and your sign-in. It
        cannot be undone. Bids already paid are not refunded.
      </p>
      <button type="button" className="pill pill-danger pill-sm" onClick={onOpen}>
        Delete account
      </button>
    </section>
  );
}
